import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Chip, CircularProgress, Stack, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useToken from '../../hooks/useToken';
import useProgressRedirect from '../../hooks/useProgressRedirect';
import { FindingFields } from '../shared/FindingDetails';
import { SEVERITY_STYLES } from '../shared/severityStyles';
import './Dashboard.css';

function SeverityCounts({ findings }) {
  const counts = {};
  findings.forEach(f => {
    const key = f.severity || 'Unspecified';
    counts[key] = (counts[key] || 0) + 1;
  });
  const order = [...Object.keys(SEVERITY_STYLES), 'Unspecified'];
  const present = order.filter(k => counts[k]);

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={2}>
      <Chip label={`${findings.length} total`} variant="outlined" />
      {present.map(k => (
        <Chip
          key={k}
          label={`${counts[k]} ${k}`}
          sx={SEVERITY_STYLES[k] ? { bgcolor: SEVERITY_STYLES[k].bg, color: SEVERITY_STYLES[k].text, fontWeight: 'bold' } : undefined}
        />
      ))}
    </Stack>
  );
}

function FindingAccordion({ finding }) {
  const severityStyle = finding.severity && SEVERITY_STYLES[finding.severity];
  const title = finding.name
    || (finding.vulnerability_type || []).join(', ')
    || 'Untitled finding';

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: '100%', pr: 1 }}>
          <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{title}</Typography>
          {finding.severity && (
            <Chip
              size="small"
              label={finding.severity}
              sx={{ bgcolor: severityStyle?.bg, color: severityStyle?.text, fontWeight: 'bold' }}
            />
          )}
          {finding.website_name && <Chip size="small" variant="outlined" label={finding.website_name} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <FindingFields finding={finding} />
      </AccordionDetails>
    </Accordion>
  );
}

function FindingsSummary() {
  const { getValidToken, removeToken } = useToken();
  const navigate = useNavigate();
  const [findings, setFindings] = useState(null);
  const [error, setError] = useState(null);

  useProgressRedirect("END");

  useEffect(() => {
    async function load() {
      const authToken = await getValidToken();
      if (!authToken) {
        removeToken();
        navigate("/", { replace: true });
        return;
      }
      try {
        const res = await fetch('/api/player/findings-summary', {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setFindings(await res.json());
      } catch (e) {
        setError(e.message);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container className="dashboard-container py-5">
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Your Submitted Findings
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        A reference summary of everything you submitted during the study.
      </Typography>

      {error && <Typography color="error">Failed to load your findings: {error}</Typography>}
      {!findings && !error && (
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} /><Typography>Loading…</Typography>
        </Box>
      )}

      {findings && findings.length === 0 && (
        <Typography color="text.secondary">You didn't submit any findings.</Typography>
      )}

      {findings && findings.length > 0 && (
        <>
          <SeverityCounts findings={findings} />
          <Stack spacing={1}>
            {findings.map(f => <FindingAccordion key={f.id} finding={f} />)}
          </Stack>
        </>
      )}
    </Container>
  );
}

export default FindingsSummary;
